import React, { useState, useEffect } from "react";
import {
    Plus, Trash2, X, FileText, Printer, Save, Download,
    ShoppingCart, Truck, User, MapPin, Search, Eye, Edit3, Loader2
} from "lucide-react";
import api from "../api/axiosInstance";

const FactoryChallan = () => {
    const [projects, setProjects] = useState([]);
    const [challans, setChallans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const initialChallan = {
        id: null,
        project_id: "",
        challan_no: "",
        challan_date: new Date().toISOString().split("T")[0],
        vehicle_no: "",
        driver_name: "",
        driver_contact: "",
        delivery_address: "",
        notes: "",
        items: [{ id: Date.now(), item_name: "", qty: 1, unit: "pcs" }]
    };

    const [formData, setFormData] = useState(initialChallan);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [pRes, cRes] = await Promise.all([
                api.get("get_projects.php"),
                api.get("get_factory_challans.php")
            ]);
            setProjects(pRes.data.projects || []);
            setChallans(cRes.data.data || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const fetchNextChallanNo = async () => {
        const res = await api.get("get_next_factory_challan_no.php");
        if (res.data.status === "success") {
            setFormData(prev => ({ ...prev, challan_no: res.data.next_no }));
        }
    };

    const handleCreate = () => {
        setIsEdit(false);
        setFormData(initialChallan);
        fetchNextChallanNo();
        setActiveModal("form");
    };

    const handleEdit = (item) => {
        setIsEdit(true);
        const items = typeof item.items_json === 'string' ? JSON.parse(item.items_json) : [];
        setFormData({ ...item, items });
        setActiveModal("form");
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this Challan permanently?")) return;
        try {
            await api.post("manage_factory_challan.php", { action: "delete", id });
            loadData();
        } catch (err) { alert("Delete failed"); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaveLoading(true);
        try {
            const res = await api.post("manage_factory_challan.php", {
                ...formData,
                action: isEdit ? "update" : "create"
            });
            if (res.data.status === "success") {
                setActiveModal(null);
                loadData();
            }
        } catch (err) { alert("Save failed"); }
        finally { setSaveLoading(false); }
    };

    const downloadPDF = async (id, no) => {
        const res = await api.get(`generate_factory_challan_pdf.php?id=${id}`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `CHALLAN_${no.replace(/\//g, "_")}.pdf`);
        link.click();
    };

    if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-300">SYNCING FACTORY LOGISTICS...</div>;

    return (
        <div className="space-y-8 p-4">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 italic uppercase tracking-tighter">Factory Dispatch</h1>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Material Outward Management</p>
                </div>
                <button onClick={handleCreate} className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl flex items-center gap-2 hover:bg-slate-900 transition-all">
                    <Plus size={18} /> New Delivery Challan
                </button>
            </div>

            {/* List Table */}
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b">
                        <tr>
                            <th className="p-6">Challan Details</th>
                            <th className="p-6">Project Site</th>
                            <th className="p-6">Transport info</th>
                            <th className="p-6 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {challans.map((item) => (
                            <tr key={item.id} className="hover:bg-slate-50/50 group transition-all">
                                <td className="p-6">
                                    <p className="font-black text-slate-900 uppercase">#{item.challan_no}</p>
                                    <p className="text-[10px] text-slate-400 font-bold">{item.challan_date}</p>
                                </td>
                                <td className="p-6 font-bold text-slate-700 uppercase text-xs">{item.project_name}</td>
                                <td className="p-6">
                                    <p className="text-xs font-black text-indigo-600 uppercase">{item.vehicle_no}</p>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase">{item.driver_name}</p>
                                </td>
                                <td className="p-6 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-amber-500"><Edit3 size={18} /></button>
                                        <button onClick={() => downloadPDF(item.id, item.challan_no)} className="p-2 text-slate-400 hover:text-indigo-600"><Download size={18} /></button>
                                        <button onClick={() => handleDelete(item.id)} className="p-2 text-slate-400 hover:text-rose-500"><Trash2 size={18} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* FORM MODAL */}
            {activeModal === "form" && (
                <div className="fixed inset-0 z-[500] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
                    <form onSubmit={handleSubmit} className="bg-white w-full max-w-5xl rounded-[48px] p-12 space-y-8 overflow-y-auto max-h-[95vh]">
                        <div className="flex justify-between items-center border-b pb-6">
                            <h2 className="text-3xl font-black italic uppercase tracking-tighter">{isEdit ? "Update" : "Issue"} Factory Challan</h2>
                            <button type="button" onClick={() => setActiveModal(null)} className="p-2 bg-slate-50 rounded-full text-rose-500"><X /></button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-slate-50 p-8 rounded-[32px]">
                            <div className="space-y-1">
                                <label className="text-[9px] font-black uppercase text-slate-400 ml-2">Challan No</label>
                                <input readOnly className="w-full bg-white p-3 rounded-xl font-bold text-indigo-600" value={formData.challan_no} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black uppercase text-slate-400 ml-2">Destination Site</label>
                                <select required className="w-full bg-white p-3 rounded-xl font-bold outline-none" value={formData.project_id} onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}>
                                    <option value="">Select Project...</option>
                                    {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black uppercase text-slate-400 ml-2">Vehicle Number</label>
                                <input placeholder="MH-04-XX-0000" className="w-full bg-white p-3 rounded-xl font-bold outline-none" value={formData.vehicle_no} onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] font-black uppercase text-slate-400 ml-2">Date</label>
                                <input type="date" className="w-full bg-white p-3 rounded-xl font-bold outline-none" value={formData.challan_date} onChange={(e) => setFormData({ ...formData, challan_date: e.target.value })} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <h3 className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-2"><MapPin size={14} /> Delivery Address</h3>
                                <textarea className="w-full bg-slate-50 p-4 rounded-3xl font-bold text-sm h-24 outline-none" placeholder="Site contact person & full address..." value={formData.delivery_address} onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })} />
                            </div>
                            <div className="space-y-4">
                                <h3 className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-2"><User size={14} /> Personnel Details</h3>
                                <div className="flex gap-4">
                                    <input placeholder="Driver Name" className="flex-1 bg-slate-50 p-4 rounded-2xl font-bold outline-none" value={formData.driver_name} onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })} />
                                    <input placeholder="Contact No" className="flex-1 bg-slate-50 p-4 rounded-2xl font-bold outline-none" value={formData.driver_contact} onChange={(e) => setFormData({ ...formData, driver_contact: e.target.value })} />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-[10px] font-black uppercase text-indigo-500 flex items-center gap-2"><ShoppingCart size={14} /> Items being dispatched</h3>
                            {formData.items.map((item, idx) => (
                                <div key={item.id} className="flex gap-4 items-end">
                                    <div className="flex-1">
                                        <input placeholder="Item Description" className="w-full p-4 bg-slate-50 rounded-2xl font-bold" value={item.item_name} onChange={(e) => {
                                            const n = [...formData.items]; n[idx].item_name = e.target.value; setFormData({ ...formData, items: n });
                                        }} />
                                    </div>
                                    <div className="w-24">
                                        <input type="number" className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-center" value={item.qty} onChange={(e) => {
                                            const n = [...formData.items]; n[idx].qty = e.target.value; setFormData({ ...formData, items: n });
                                        }} />
                                    </div>
                                    <button type="button" onClick={() => setFormData({ ...formData, items: formData.items.filter(i => i.id !== item.id) })} className="p-4 text-rose-400 hover:text-rose-600"><Trash2 size={20} /></button>
                                </div>
                            ))}
                            <button type="button" onClick={() => setFormData({ ...formData, items: [...formData.items, { id: Date.now(), item_name: "", qty: 1, unit: "pcs" }] })} className="w-full py-4 border-2 border-dashed border-slate-100 rounded-3xl text-[10px] font-black uppercase text-slate-400">+ Add Dispatch Line</button>
                        </div>

                        <button disabled={saveLoading} className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl flex items-center justify-center gap-3 hover:bg-indigo-600 transition-all uppercase italic">
                            {saveLoading ? <Loader2 className="animate-spin" /> : <Save size={24} />}
                            {isEdit ? "Update Challan" : "Generate Delivery Challan"}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default FactoryChallan;